/**
 * Pending trip context — the bridge between anonymous content surfaces
 * (blog articles, the free preview, the public samples) and the planner.
 *
 * A reader who answers "when are you going?" on an article should never be
 * asked again: the destination and dates they gave are stashed here, picked up
 * by the preview widget, and are available to replay into a real
 * ItineraryRequest once they register.
 */

const KEY = 'pending_trip_context';
const SESSION_KEY = 'is_anon_session_id';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days — travel research runs long

const DAY_MS = 24 * 60 * 60 * 1000;

/** Format a Date as YYYY-MM-DD in local time (toISOString would shift the day). */
export function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * A rolling near-future stay window, used when a surface carries no dates of
 * its own. Booking.com returns live availability with prices for a dated
 * search and an empty results page for an undated one, so we always send dates.
 */
export function defaultStayWindow(nights = 3, daysAhead = 30) {
  const checkIn = new Date(Date.now() + daysAhead * DAY_MS);
  const checkOut = new Date(checkIn.getTime() + nights * DAY_MS);
  return { checkIn: toISODate(checkIn), checkOut: toISODate(checkOut) };
}

export function getPendingTripContext() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const ctx = JSON.parse(raw);
    if (!ctx || !ctx.destination) return null;
    if (ctx.savedAt && Date.now() - ctx.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return ctx;
  } catch {
    return null;
  }
}

/**
 * Merge into the stored context. Partial updates are normal: the article sets
 * the destination first, then the date pill fills in the dates.
 */
export function setPendingTripContext(patch) {
  try {
    const current = getPendingTripContext() || {};
    const next = { ...current, ...patch, savedAt: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(next));
    return next;
  } catch {
    // localStorage full or disabled — the bridge degrades to "type it again"
    return null;
  }
}

/**
 * A stable per-browser id recorded on every affiliate click and replayed on
 * registration, so the anonymous path — article read, booking click, preview,
 * signup, itinerary — can be joined end to end. It identifies a browser, not a
 * person: no personal data is stored in it and it is never sent to a partner.
 */
export function getAnonSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      const rand =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID().replace(/-/g, '').slice(0, 20)
          : Math.random().toString(36).slice(2, 12) + Math.random().toString(36).slice(2, 12);
      id = `a${Date.now().toString(36)}${rand}`.slice(0, 48);
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // No storage — the click is still tracked, just not joinable to a signup.
    return null;
  }
}

export function clearPendingTripContext() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
